import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { PRODUCTS, ProductItem, launchUrl } from "../../data/productsData";
import { loadProducts } from "../../services/productSource";
import "../../styles/revamp.css";

/**
 * The tabbed product suite. Six systems, one panel each.
 *
 * Tab colour is the product's signal hue, so the strip of tabs doubles as the
 * legend for the rest of the page. Metrics are set in monospace because they are
 * instrument readings — the same figures in the body face read as marketing
 * claims, which is the wrong register for the technical buyer this page is for.
 *
 * Starts from the static registry so the section paints immediately, then swaps
 * in CMS content when it arrives. There is no loading state because there is
 * never nothing to show.
 */

const ProductsTabSection: React.FC = () => {
    const [products, setProducts] = useState<ProductItem[]>(PRODUCTS);
    const [active, setActive] = useState(0);
    const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

    useEffect(() => {
        let cancelled = false;
        loadProducts().then((next) => {
            if (!cancelled) setProducts(next);
        });
        return () => {
            cancelled = true;
        };
    }, []);

    const product = products[active];

    // Arrow-key movement between tabs, which is what a tablist is expected to do
    // and what a row of buttons does not do on its own.
    const onKeyDown = (e: React.KeyboardEvent) => {
        const last = products.length - 1;
        let next: number | null = null;

        if (e.key === "ArrowRight") next = active === last ? 0 : active + 1;
        if (e.key === "ArrowLeft") next = active === 0 ? last : active - 1;
        if (e.key === "Home") next = 0;
        if (e.key === "End") next = last;

        if (next !== null) {
            e.preventDefault();
            setActive(next);
            tabRefs.current[next]?.focus();
        }
    };

    if (!product) return null;

    return (
        <section className="mx-suite">
            <div className="container">
                <p className="mx-eyebrow">The platform</p>
                {/* Deliberately not "N systems" — a count in the copy is a
                    thing to forget when a product is added. */}
                <h2 className="mx-suite-title">
                    One engineering team. Every system in production.
                </h2>

                <div
                    className="mx-tabs"
                    role="tablist"
                    aria-label="MindSyncX products"
                    onKeyDown={onKeyDown}
                >
                    {products.map((p, i) => (
                        <button
                            key={p.slug}
                            ref={(el) => {
                                tabRefs.current[i] = el;
                            }}
                            role="tab"
                            type="button"
                            id={`mx-tab-${p.slug}`}
                            aria-selected={i === active}
                            aria-controls={`mx-panel-${p.slug}`}
                            tabIndex={i === active ? 0 : -1}
                            className="mx-tab"
                            data-active={i === active}
                            style={{ ["--mx-dot-color" as any]: p.accent }}
                            onClick={() => setActive(i)}
                        >
                            <span className="mx-dot" />
                            {p.name}
                        </button>
                    ))}
                </div>

                <div
                    className="mx-suite-panel"
                    role="tabpanel"
                    id={`mx-panel-${product.slug}`}
                    aria-labelledby={`mx-tab-${product.slug}`}
                    style={{ ["--mx-dot-color" as any]: product.accent }}
                >
                    <div className="mx-suite-panel-main">
                        <p className="mx-eyebrow">{product.category}</p>
                        <h3 className="mx-suite-panel-title">{product.tagline}</h3>
                        <p className="mx-suite-panel-body">{product.summary}</p>

                        <div className="mx-suite-actions">
                            <Link className="mx-btn mx-btn-primary" to={`/products/${product.slug}`}>
                                View details
                            </Link>
                            {launchUrl(product) && (
                                <a
                                    className="mx-btn mx-btn-ghost"
                                    href={launchUrl(product)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={`Launch ${product.name} in a new tab`}
                                >
                                    Launch app ↗
                                </a>
                            )}
                        </div>
                    </div>

                    <div className="mx-suite-panel-side">
                        <ul className="mx-metrics">
                            {product.metrics.map((m) => (
                                <li key={m.label}>
                                    <span className="mx-metric-value">{m.value}</span>
                                    <span className="mx-metric-label">{m.label}</span>
                                </li>
                            ))}
                        </ul>

                        <ul className="mx-capabilities">
                            {product.features.slice(0, 4).map((f) => (
                                <li key={f.title}>
                                    <span className="mx-dot" />
                                    {f.title}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ProductsTabSection;
